import { cache } from '../db/state';
import { DB } from '../lib';
import { resolveBibleDefaults } from './bibleLocaleDefaults';

declare const BIBLE_DATA_BASE: string;

const dataUrl = (p: string) => BIBLE_DATA_BASE + '/' + p;

interface BibleVersion {
  id: string;
  name: string;
  language: string;
  languageName: string;
  url: string;
  bookUrl: string;
  bookIntroUrl?: string;
  footnoteUrl?: string;
  outlineUrl?: string;
  topicUrl?: string;
}

interface BibleBook {
  _id: string;
  book_id: string;
  name: string;
  acronym_name: string;
}

interface BibleBookIntro {
  _id: string;
  book_index: string;
  type: string;
  intro: string;
}

interface BibleFootnote {
  _id: string;
  book_index: string;
  chapter: string;
  section: string;
  flag: string;
  location: string;
  seq: string;
  note: string;
}

interface BibleOutline {
  _id: string;
  language: string;
  book_index: string;
  chapter: string;
  section: string;
  flag: string;
  level: string;
  outline: string;
}

interface BibleTopic {
  _id: string;
  book_index: string;
  topic: string;
}

interface BibleVerse {
  _id: string;
  Book: string;
  Chapter: string;
  Verse: string;
  Flag: string;
  Text: string;
}

const BIBLE_VERSIONS: BibleVersion[] = [
  {
    id: 'ChiRcVs', name: '恢复注解版(简体)', language: 'zh', languageName: '中文',
    url: dataUrl('zh/ChiRcVs/ChiRcVs.csv'),
    bookUrl: dataUrl('zh/ChiRcVs/book_name.csv'),
    bookIntroUrl: dataUrl('zh/ChiRcVs/book_intro.csv'),
    footnoteUrl: dataUrl('zh/ChiRcVs/footnote.csv'),
    outlineUrl: dataUrl('zh/ChiRcVs/outline.csv'),
    topicUrl: dataUrl('zh/ChiRcVs/topic.csv'),
  },
  {
    id: 'ChiUns', name: '和合本(简体)', language: 'zh', languageName: '中文',
    url: dataUrl('zh/ChiUns/ChiUns.csv'),
    bookUrl: dataUrl('zh/ChiUns/book_name.csv'),
  },
  {
    id: 'ChiRcV', name: '恢復註解版(繁體)', language: 'zh-hant', languageName: '中文',
    url: dataUrl('zh-hant/ChiRcV/ChiRcV.csv'),
    bookUrl: dataUrl('zh-hant/ChiRcV/book_name.csv'),
    bookIntroUrl: dataUrl('zh-hant/ChiRcV/book_intro.csv'),
    footnoteUrl: dataUrl('zh-hant/ChiRcV/footnote.csv'),
    outlineUrl: dataUrl('zh-hant/ChiRcV/outline.csv'),
    topicUrl: dataUrl('zh-hant/ChiRcV/topic.csv'),
  },
  {
    id: 'ChiUn', name: '和合本(繁體)', language: 'zh-hant', languageName: '中文',
    url: dataUrl('zh-hant/ChiUn/ChiUn.csv'),
    bookUrl: dataUrl('zh-hant/ChiUn/book_name.csv'),
  },
  {
    id: 'CzeBKR', name: 'Bible Kralická', language: 'cs', languageName: 'čeština',
    url: dataUrl('cs/CzeBKR/CzeBKR.csv'),
    bookUrl: dataUrl('cs/CzeBKR/book_name.csv'),
  },
  {
    id: 'DaOT1871NT1907', name: 'Den danske bibel 1871/1907', language: 'da', languageName: 'dansk',
    url: dataUrl('da/DaOT1871NT1907/DaOT1871NT1907.csv'),
    bookUrl: dataUrl('da/DaOT1871NT1907/book_name.csv'),
  },
  {
    id: 'GerElb1905', name: 'Elberfelder Bibel 1905', language: 'de', languageName: 'Deutsch',
    url: dataUrl('de/GerElb1905/GerElb1905.csv'),
    bookUrl: dataUrl('de/GerElb1905/book_name.csv'),
  },
  {
    id: 'GreVamvas', name: 'Ελληνική μετάφραση', language: 'el', languageName: 'Ελληνικά',
    url: dataUrl('el/GreVamvas/GreVamvas.csv'),
    bookUrl: dataUrl('el/GreVamvas/book_name.csv'),
  },
  {
    id: 'KJV', name: 'KJV', language: 'en', languageName: 'English',
    url: dataUrl('en/KJV/KJV.csv'),
    bookUrl: dataUrl('en/KJV/book_name.csv'),
  },
  {
    id: 'Darby', name: 'Darby Bible', language: 'en', languageName: 'English',
    url: dataUrl('en/Darby/Darby.csv'),
    bookUrl: dataUrl('en/Darby/book_name.csv'),
  },
  {
    id: 'ASV', name: 'American Standard Version', language: 'en', languageName: 'English',
    url: dataUrl('en/ASV/ASV.csv'),
    bookUrl: dataUrl('en/ASV/book_name.csv'),
  },
  {
    id: 'SpaRV', name: 'Reina-Valera', language: 'es', languageName: 'español',
    url: dataUrl('es/SpaRV/SpaRV.csv'),
    bookUrl: dataUrl('es/SpaRV/book_name.csv'),
  },
  {
    id: 'FinPR', name: 'Pyhä Raamattu', language: 'fi', languageName: 'suomi',
    url: dataUrl('fi/FinPR/FinPR.csv'),
    bookUrl: dataUrl('fi/FinPR/book_name.csv'),
  },
  {
    id: 'FreSegond1910', name: 'La Segond 1910', language: 'fr', languageName: 'français',
    url: dataUrl('fr/FreSegond1910/FreSegond1910.csv'),
    bookUrl: dataUrl('fr/FreSegond1910/book_name.csv'),
  },
  {
    id: 'HunKar', name: 'Károli Gáspár-féle fordítás', language: 'hu', languageName: 'magyar',
    url: dataUrl('hu/HunKar/HunKar.csv'),
    bookUrl: dataUrl('hu/HunKar/book_name.csv'),
  },
  {
    id: 'ItaRive', name: 'Riva 1861', language: 'it', languageName: 'italiano',
    url: dataUrl('it/ItaRive/ItaRive.csv'),
    bookUrl: dataUrl('it/ItaRive/book_name.csv'),
  },
  {
    id: 'JapKougo', name: '口語訳聖書', language: 'ja', languageName: '日本語',
    url: dataUrl('ja/JapKougo/JapKougo.csv'),
    bookUrl: dataUrl('ja/JapKougo/book_name.csv'),
  },
  {
    id: 'KorRV', name: '개역한글판 성경', language: 'ko', languageName: '한국어',
    url: dataUrl('ko/KorRV/korRV.csv'),
    bookUrl: dataUrl('ko/KorRV/book_name.csv'),
  },
  {
    id: 'LtKBB', name: 'Lietuviškas Krikščionio Biblinis Balsas', language: 'lt', languageName: 'lietuvių kalba',
    url: dataUrl('lt/LtKBB/LtKBB.csv'),
    bookUrl: dataUrl('lt/LtKBB/book_name.csv'),
  },
  {
    id: 'LvGluck8', name: 'Glika Bībele 8. izdevums', language: 'lv', languageName: 'latviešu valoda',
    url: dataUrl('lv/LvGluck8/LvGluck8.csv'),
    bookUrl: dataUrl('lv/LvGluck8/book_name.csv'),
  },
  {
    id: 'BurJudson', name: 'ျမန္မာက်မ္း (ဂ်ဒ်ဆင္)', language: 'my', languageName: 'မြန်မာစာ',
    url: dataUrl('my/BurJudson/BurJudson.csv'),
    bookUrl: dataUrl('my/BurJudson/book_name.csv'),
  },
  {
    id: 'DutSVV', name: 'Statenvertaling', language: 'nl', languageName: 'Nederlands',
    url: dataUrl('nl/DutSVV/DutSVV.csv'),
    bookUrl: dataUrl('nl/DutSVV/book_name.csv'),
  },
  {
    id: 'Norsk', name: 'Norsk', language: 'no', languageName: 'Norsk',
    url: dataUrl('no/Norsk/Norsk.csv'),
    bookUrl: dataUrl('no/Norsk/book_name.csv'),
  },
  {
    id: 'PolUGdanska', name: 'Uwspółcześniona Biblia Gdańska', language: 'pl', languageName: 'polski',
    url: dataUrl('pl/PolUGdanska/PolUGdanska.csv'),
    bookUrl: dataUrl('pl/PolUGdanska/book_name.csv'),
  },
  {
    id: 'PorNVA', name: 'Bíblia Nova Versão de Acesso Livre', language: 'pt', languageName: 'português',
    url: dataUrl('pt/PorNVA/PorNVA.csv'),
    bookUrl: dataUrl('pt/PorNVA/book_name.csv'),
  },
  {
    id: 'RusSynodal', name: 'Синодальный перевод', language: 'ru', languageName: 'русский язык',
    url: dataUrl('ru/RusSynodal/RusSynodal.csv'),
    bookUrl: dataUrl('ru/RusSynodal/book_name.csv'),
  },
  {
    id: 'SloChraska', name: 'Sveto pismo Starega in Novega zakona', language: 'sl', languageName: 'slovenščina',
    url: dataUrl('sl/SloChraska/SloChraska.csv'),
    bookUrl: dataUrl('sl/SloChraska/book_name.csv'),
  },
  {
    id: 'Alb', name: 'Bibla e Shenjtë', language: 'sq', languageName: 'shqip',
    url: dataUrl('sq/Alb/Alb.csv'),
    bookUrl: dataUrl('sq/Alb/book_name.csv'),
  },
  {
    id: 'SweFolk', name: 'Svenska Folkbibeln', language: 'sv', languageName: 'svenska',
    url: dataUrl('sv/SweFolk/SweFolk.csv'),
    bookUrl: dataUrl('sv/SweFolk/book_name.csv'),
  },
  {
    id: 'Viet', name: 'tiếng Việt', language: 'vi', languageName: 'tiếng Việt',
    url: dataUrl('vi/Viet/Viet.csv'),
    bookUrl: dataUrl('vi/Viet/book_name.csv'),
  },
];

const SCHEMA_VERSION = 'v9';
const getBibleDataKey = (versionId: string, type: string): string => `bible/${SCHEMA_VERSION}/${versionId}/${type}`;
const getBibleSearchIndexKey = (versionId: string): string => `bible/${SCHEMA_VERSION}/${versionId}/search_index`;
const getBibleMetaKey = (versionId: string): string => `bible/${SCHEMA_VERSION}/${versionId}/meta`;
const getBibleVerseChunkKey = (versionId: string, bookField: string): string => `bible/${SCHEMA_VERSION}/${versionId}/verses/${bookField}`;
const getBibleCacheKeys = (versionId: string): string[] => [
  getBibleDataKey(versionId, 'books'),
  getBibleDataKey(versionId, 'bookIntros'),
  getBibleDataKey(versionId, 'footnotes'),
  getBibleDataKey(versionId, 'outlines'),
  getBibleDataKey(versionId, 'topics'),
  getBibleSearchIndexKey(versionId),
  getBibleMetaKey(versionId),
];

const splitDelimitedLine = (line: string, expectedColumns?: number): string[] => {
  const normalize = (parts: string[]) => parts.map(part => part.trim().replace(/^"|"$/g, ''));
  if (line.includes('\t')) return normalize(line.split('\t'));
  if (expectedColumns && expectedColumns > 0) {
    const parts = line.split(',');
    if (parts.length > expectedColumns) {
      const fixed = parts.slice(0, expectedColumns - 1);
      const tail = parts.slice(expectedColumns - 1).join(',');
      return normalize([...fixed, tail]);
    }
    return normalize(parts);
  }
  const matches = line.match(/"([^"]*)"|([^,]+)/g);
  if (!matches) return [];
  return normalize(matches);
};

const parseCSV = (csvText: string, versionId: string): BibleVerse[] => {
  const lines = csvText.trim().split('\n');
  if (lines.length === 0) return [];
  const header = splitDelimitedLine(lines[0]).map(col => col.toLowerCase());
  const dataLines = lines.slice(1);
  const headerHasId = header.includes('_id') || header.includes('id');
  const headerBookIndex = header.findIndex(col => col === 'book' || col === 'book_id');
  const headerChapterIndex = header.findIndex(col => col === 'chapter');
  const headerVerseIndex = header.findIndex(col => col === 'verse');
  const headerFlagIndex = header.findIndex(col => col === 'flag');
  const headerTextIndex = header.findIndex(col => col === 'text' || col === 'content');
  return dataLines.map((line, rowIndex) => {
    const fallbackExpectedColumns = headerFlagIndex >= 0 ? 6 : (headerHasId ? 5 : 4);
    const values = splitDelimitedLine(line, header.length > 0 ? header.length : fallbackExpectedColumns);
    if (values.length === 0) return {} as BibleVerse;
    const hasFlag = headerFlagIndex >= 0;
    const hasId = headerHasId || values.length >= 5;
    const resolvedId = hasId ? (values[0] || `${versionId}-${rowIndex + 1}`) : `${versionId}-${values[0] || rowIndex + 1}-${values[1] || ''}-${values[2] || ''}`;
    const resolvedBookIndex = headerBookIndex >= 0 ? headerBookIndex : (hasId ? 1 : 0);
    const resolvedChapterIndex = headerChapterIndex >= 0 ? headerChapterIndex : (hasId ? 2 : 1);
    const resolvedVerseIndex = headerVerseIndex >= 0 ? headerVerseIndex : (hasId ? 3 : 2);
    const resolvedFlagIndex = headerFlagIndex >= 0 ? headerFlagIndex : -1;
    const resolvedTextIndex = headerTextIndex >= 0 ? headerTextIndex : (hasFlag ? (hasId ? 5 : 4) : (hasId ? 4 : 3));
    return {
      _id: resolvedId, Book: values[resolvedBookIndex] || '',
      Chapter: values[resolvedChapterIndex] || '', Verse: values[resolvedVerseIndex] || '',
      Flag: resolvedFlagIndex >= 0 ? (values[resolvedFlagIndex] || '0') : '0',
      Text: values[resolvedTextIndex] || '', versionId
    };
  }).filter(verse => verse._id !== '');
};

const parseBookCSV = (csvText: string, versionId: string): BibleBook[] => {
  const lines = csvText.trim().split('\n');
  return lines.slice(1).map(line => {
    const values = splitDelimitedLine(line);
    return { _id: values[0] || '', book_id: values[1] || '', name: values[2] || '', acronym_name: values[3] || '', versionId };
  }).filter(verse => verse._id !== '');
};

const parseBookIntroCSV = (csvText: string, versionId: string): BibleBookIntro[] => {
  const lines = csvText.trim().split('\n');
  return lines.slice(1).map(line => {
    const values = splitDelimitedLine(line);
    return { _id: values[0] || '', book_index: values[1] || '', type: values[2] || '', intro: values[3] || '', versionId };
  }).filter(verse => verse._id !== '');
};

const parseBookOutlineCSV = (csvText: string, versionId: string): BibleFootnote[] => {
  const lines = csvText.trim().split('\n');
  return lines.slice(1).map(line => {
    const values = splitDelimitedLine(line);
    return { _id: values[0] || '', book_index: values[1] || '', chapter: values[2] || '', section: values[3] || '', flag: values[4] || '', location: values[5] || '', seq: values[6] || '', note: values[7] || '', versionId };
  }).filter(verse => verse._id !== '');
};

const parseBookFootnoteCSV = (csvText: string, versionId: string): BibleOutline[] => {
  const lines = csvText.trim().split('\n');
  return lines.slice(1).map(line => {
    const values = splitDelimitedLine(line);
    return { _id: values[0] || '', language: values[1] || '', book_index: values[2] || '', chapter: values[3] || '', section: values[4] || '', flag: values[5] || '', level: values[6] || '', outline: values[7] || '', versionId };
  }).filter(verse => verse._id !== '');
};

const parseBookTopicCSV = (csvText: string, versionId: string): BibleTopic[] => {
  const lines = csvText.trim().split('\n');
  return lines.slice(1).map(line => {
    const values = splitDelimitedLine(line);
    return { _id: values[0] || '', book_index: values[1] || '', topic: values[2] || '', versionId };
  }).filter(verse => verse._id !== '');
};

export const getAvailableVersions = (): BibleVersion[] => BIBLE_VERSIONS;
export const getVersionsByLanguage = (language: string): BibleVersion[] => BIBLE_VERSIONS.filter(version => version.language === language);
export const getVersionById = (versionId: string): BibleVersion | undefined => BIBLE_VERSIONS.find(version => version.id === versionId);

interface BibleMeta { chunks: string[]; totalVerses: number; lastModified?: number; }

export const loadBibleData = async (versionId: string): Promise<any> => {
  const version = getVersionById(versionId);
  if (!version) throw new Error(`Invalid Bible version: ${versionId}`);
  const dataKey = getBibleDataKey(versionId, 'verses');
  const bookKey = getBibleDataKey(versionId, 'books');
  const bible = {} as any;
  const cachedData = DB.get(cache, dataKey) as BibleVerse[] | undefined;
  const legacyDataKey = `bible/v8/${versionId}/verses`;
  const legacyCachedData = DB.get(cache, legacyDataKey as any) as BibleVerse[] | undefined;
  const cachedBooks = DB.get(cache, bookKey) as BibleBook[] | undefined;
  if (cachedBooks) {
    console.log(`[Bible] ${version.name}: books from cache`);
    bible.books = cachedBooks;
  } else {
    try {
      console.log(`[Bible] ${version.name}: books from URL`);
      const response = await fetch(version.bookUrl, { method: 'GET', mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw new Error(`Failed to fetch ${version.name} Bible data: ${response.status}`);
      const csvText = await response.text();
      const books = parseBookCSV(csvText, versionId);
      DB.put(cache, bookKey, books);
      bible.books = books;
    } catch (error) {
      console.error(`[Bible] Error loading ${version.name}:`, error);
      if (cachedBooks) { console.log(`[Bible] Returning cached ${version.name} despite error`); bible.books = cachedBooks; }
      throw error;
    }
  }
  const metaKey = getBibleMetaKey(versionId);
  const meta = DB.get(cache, metaKey) as BibleMeta | undefined;
  const migrateToChunks = (verses: BibleVerse[], oldKey?: string) => {
    const bookGroups = new Map<string, BibleVerse[]>();
    for (const verse of verses) {
      const bf = verse.Book;
      if (!bookGroups.has(bf)) bookGroups.set(bf, []);
      bookGroups.get(bf)!.push(verse);
    }
    for (const [bookField, chunk] of bookGroups) {
      DB.put(cache, getBibleVerseChunkKey(versionId, bookField), chunk);
    }
    const newMeta: BibleMeta = { chunks: Array.from(bookGroups.keys()), totalVerses: verses.length, lastModified: Date.now() };
    DB.put(cache, metaKey, newMeta);
    if (oldKey) DB.del(cache, oldKey as any);
    createAndStoreSearchIndex(verses, versionId);
    return verses;
  };
  if (meta) {
    console.log(`[Bible] ${version.name}: verses from chunks (${meta.chunks.length} books)`);
    const allVerses: BibleVerse[] = [];
    for (const bookField of meta.chunks) {
      const chunk = DB.get(cache, getBibleVerseChunkKey(versionId, bookField)) as BibleVerse[] | undefined;
      if (chunk) allVerses.push(...chunk);
    }
    bible.verses = allVerses;
  } else if (legacyCachedData) {
    console.log(`[Bible] ${version.name}: migrating v8 → v9 chunks`);
    bible.verses = migrateToChunks(legacyCachedData, legacyDataKey);
  } else if (cachedData) {
    console.log(`[Bible] ${version.name}: migrating flat → chunks`);
    bible.verses = migrateToChunks(cachedData, dataKey);
  } else {
    try {
      console.log(`[Bible] ${version.name}: verses from URL`);
      const response = await fetch(version.url, { method: 'GET', mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw new Error(`Failed to fetch ${version.name} verses: ${response.status}`);
      const csvText = await response.text();
      const bibleData = parseCSV(csvText, versionId);
      bible.verses = migrateToChunks(bibleData);
    } catch (error) {
      console.error(`[Bible] Error loading ${version.name}:`, error);
      if (cachedData) { console.log(`[Bible] Returning cached ${version.name} despite error`); bible.verses = cachedData; }
      throw error;
    }
  }
  // 并行加载补充数据
  const [bookIntros, footnotes, outlines, topics] = await Promise.all([
    loadSupplemental(versionId, version.bookIntroUrl, getBibleDataKey(versionId, 'bookIntros'), parseBookIntroCSV),
    loadSupplemental(versionId, version.footnoteUrl, getBibleDataKey(versionId, 'footnotes'), parseBookOutlineCSV),
    loadSupplemental(versionId, version.outlineUrl, getBibleDataKey(versionId, 'outlines'), parseBookFootnoteCSV),
    loadSupplemental(versionId, version.topicUrl, getBibleDataKey(versionId, 'topics'), parseBookTopicCSV),
  ]);
  if (bookIntros) bible.bookIntros = bookIntros;
  if (footnotes) bible.footnotes = footnotes;
  if (outlines) bible.outlines = outlines;
  if (topics) bible.topics = topics;
  return bible;
};

const fetchWithTimeout = (url: string, timeoutMs = 15000): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit', signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

// 加载单个补充数据文件，优先从缓存读取
const loadSupplemental = async (
  versionId: string, url: string | undefined, key: string,
  parser: (csvText: string, versionId: string) => any[],
) => {
  if (!url) return undefined;
  const cached = DB.get(cache, key);
  if (cached) return cached;
  try {
    const r = await fetchWithTimeout(url);
    if (r.ok) {
      const txt = await r.text();
      const dd = parser(txt, versionId);
      DB.put(cache, key, dd);
      return dd;
    }
  } catch {}
  return undefined;
};

export const loadBibleMetadata = async (versionId: string): Promise<any> => {
  const version = getVersionById(versionId);
  if (!version) throw new Error(`Invalid Bible version: ${versionId}`);
  const bookKey = getBibleDataKey(versionId, 'books');
  const bible = {} as any;
  let cachedBooks = DB.get(cache, bookKey) as BibleBook[] | undefined;
  if (cachedBooks) { bible.books = cachedBooks; }
  else {
    const response = await fetchWithTimeout(version.bookUrl, 20000);
    if (!response.ok) throw new Error(`Failed to fetch ${version.name} Bible books: ${response.status}`);
    const csvText = await response.text();
    const books = parseBookCSV(csvText, versionId);
    DB.put(cache, bookKey, books);
    bible.books = books;
  }
  // 并行加载补充数据，不因个别文件加载慢而阻塞整体
  const [bookIntros, footnotes, outlines, topics] = await Promise.all([
    loadSupplemental(versionId, version.bookIntroUrl, getBibleDataKey(versionId, 'bookIntros'), parseBookIntroCSV),
    loadSupplemental(versionId, version.footnoteUrl, getBibleDataKey(versionId, 'footnotes'), parseBookOutlineCSV),
    loadSupplemental(versionId, version.outlineUrl, getBibleDataKey(versionId, 'outlines'), parseBookFootnoteCSV),
    loadSupplemental(versionId, version.topicUrl, getBibleDataKey(versionId, 'topics'), parseBookTopicCSV),
  ]);
  if (bookIntros) bible.bookIntros = bookIntros;
  if (footnotes) bible.footnotes = footnotes;
  if (outlines) bible.outlines = outlines;
  if (topics) bible.topics = topics;
  return bible;
};

const resolveBookAliases = (versionId: string, bookField: string): string[] => {
  const aliases: string[] = [String(bookField)];
  const bookKey = getBibleDataKey(versionId, 'books');
  const books = DB.get(cache, bookKey) as BibleBook[] | undefined;
  if (!books) return aliases;
  const num = parseInt(bookField, 10);
  if (!isNaN(num) && num > 0 && num <= books.length) {
    const e = books[num - 1];
    if (e) {
      [e.book_id, e._id, e.name, e.acronym_name].filter(Boolean).forEach(a => { const s = String(a); if (!aliases.includes(s)) aliases.push(s); });
    }
  }
  return aliases;
};

export const loadBibleBook = async (versionId: string, bookField: string): Promise<BibleVerse[]> => {
  const aliases = resolveBookAliases(versionId, bookField);
  for (const alias of aliases) {
    const chunkKey = getBibleVerseChunkKey(versionId, alias);
    const cached = DB.get(cache, chunkKey) as BibleVerse[] | undefined;
    if (cached) return cached;
  }
  await loadBibleData(versionId);
  for (const alias of aliases) {
    const chunkKey = getBibleVerseChunkKey(versionId, alias);
    const reloaded = DB.get(cache, chunkKey) as BibleVerse[] | undefined;
    if (reloaded) return reloaded;
  }
  return [];
};

export const loadMultipleBibleVersions = async (versionIds: string[]): Promise<Map<string, BibleVerse[]>> => {
  const results = new Map<string, BibleVerse[]>();
  await Promise.all(versionIds.map(async (versionId) => {
    try { const data = await loadBibleData(versionId); results.set(versionId, data); }
    catch (error) { console.error(`Failed to load version ${versionId}:`, error); }
  }));
  return results;
};

const createAndStoreSearchIndex = (verses: BibleVerse[], versionId: string): void => {
  const index: Record<string, Set<string>> = {};
  verses.forEach(verse => {
    const chars = verse.Text.replace(/\s+/g, '').split('');
    chars.forEach(char => { if (!index[char]) index[char] = new Set(); index[char].add(verse._id); });
  });
  const serializableIndex: Record<string, string[]> = {};
  Object.keys(index).forEach(char => { serializableIndex[char] = Array.from(index[char]); });
  DB.put(cache, getBibleSearchIndexKey(versionId), serializableIndex);
};

export const getBibleVerseById = async (_id: string, versionId: string): Promise<BibleVerse | undefined> => {
  const bible = await loadBibleData(versionId);
  return (bible.verses || []).find((verse: { _id: string; }) => verse._id === _id);
};

export const searchBibleContent = async (keyword: string, versionId: string, useIndex: boolean = true): Promise<BibleVerse[]> => {
  const bible = await loadBibleData(versionId);
  const allVerses = bible.verses || [];
  if (!keyword.trim()) return [];
  if (!useIndex) return allVerses.filter((verse: { Text: string | string[]; }) => verse.Text.includes(keyword));
  try {
    const indexKey = getBibleSearchIndexKey(versionId);
    const searchIndex = DB.get(cache, indexKey) as Record<string, string[]> | undefined;
    if (!searchIndex) { createAndStoreSearchIndex(allVerses, versionId); return allVerses.filter((verse: { Text: string | string[]; }) => verse.Text.includes(keyword)); }
    const firstChar = keyword.charAt(0);
    const candidateIds = searchIndex[firstChar] || [];
    return allVerses.filter((verse: { _id: string; Text: string | string[]; }) => candidateIds.includes(verse._id) && verse.Text.includes(keyword));
  } catch (error) {
    console.error(`Search error in version ${versionId}:`, error);
    return allVerses.filter((verse: { Text: string | string[]; }) => verse.Text.includes(keyword));
  }
};

export const clearBibleCache = (versionId?: string): void => {
  const clearVersion = (vid: string) => {
    getBibleCacheKeys(vid).forEach((key) => { DB.del(cache, key as any); });
    const metaKey = getBibleMetaKey(vid);
    const meta = DB.get(cache, metaKey) as BibleMeta | undefined;
    if (meta) { for (const bookField of meta.chunks) { DB.del(cache, getBibleVerseChunkKey(vid, bookField) as any); } DB.del(cache, metaKey as any); }
  };
  if (versionId) { clearVersion(versionId); console.log(`Cleared cache for Bible version: ${versionId}`); }
  else { BIBLE_VERSIONS.forEach(version => clearVersion(version.id)); console.log('Cleared cache for all Bible versions'); }
};

export const refreshBibleData = async (versionId: string): Promise<any> => {
  clearBibleCache(versionId);
  return loadBibleData(versionId);
};

export const getBookName = (bible: any, bookId: string): string => {
  if (!bookId) return `Book ${bookId}`;
  const books: BibleBook[] = bible.books || [];
  if (!books.length) return bookId;
  let num = parseInt(bookId, 10);
  if (isNaN(num) || num <= 0) {
    num = ENGLISH_BOOK_TO_NUM[bookId] || 0;
  }
  if (num > 0) {
    let book = books.find((b) => parseInt(b.book_id, 10) === num || parseInt(b._id, 10) === num);
    if (!book && num <= books.length) book = books[num - 1];
    if (book) return book.name || book.book_id;
  }
  return bookId;
};

export const getDefaultVersionId = (locale: string): string => resolveBibleDefaults(locale).versionId;

const ENGLISH_BOOK_TO_NUM: Record<string, number> = {
  'Genesis': 1, 'Exodus': 2, 'Leviticus': 3, 'Numbers': 4, 'Deuteronomy': 5,
  'Joshua': 6, 'Judges': 7, 'Ruth': 8, 'I Samuel': 9, 'II Samuel': 10,
  'I Kings': 11, 'II Kings': 12, 'I Chronicles': 13, 'II Chronicles': 14,
  'Ezra': 15, 'Nehemiah': 16, 'Esther': 17, 'Job': 18,
  'Psalms': 19, 'Proverbs': 20, 'Ecclesiastes': 21, 'Song of Solomon': 22,
  'Isaiah': 23, 'Jeremiah': 24, 'Lamentations': 25, 'Ezekiel': 26, 'Daniel': 27,
  'Hosea': 28, 'Joel': 29, 'Amos': 30, 'Obadiah': 31, 'Jonah': 32,
  'Micah': 33, 'Nahum': 34, 'Habakkuk': 35, 'Zephaniah': 36, 'Haggai': 37,
  'Zechariah': 38, 'Malachi': 39,
  'Matthew': 40, 'Mark': 41, 'Luke': 42, 'John': 43, 'Acts': 44,
  'Romans': 45, 'I Corinthians': 46, 'II Corinthians': 47, 'Galatians': 48,
  'Ephesians': 49, 'Philippians': 50, 'Colossians': 51,
  'I Thessalonians': 52, 'II Thessalonians': 53,
  'I Timothy': 54, 'II Timothy': 55, 'Titus': 56, 'Philemon': 57,
  'Hebrews': 58, 'James': 59, 'I Peter': 60, 'II Peter': 61,
  'I John': 62, 'II John': 63, 'III John': 64, 'Jude': 65,
  'Revelation of John': 66,
};

const resolveBookNum = (book: string): number => {
  const num = parseInt(book, 10);
  if (!isNaN(num)) return num;
  return ENGLISH_BOOK_TO_NUM[book] || 0;
};

export const getVerseByReference = async (versionId: string, book: string, chapter: string, verse: string): Promise<BibleVerse | undefined> => {
  const bookNum = resolveBookNum(book);
  const chapterNum = parseInt(chapter, 10);
  const verseNum = parseInt(verse, 10);
  if (!bookNum) return undefined;
  const bibleMeta = await loadBibleMetadata(versionId);
  const books = bibleMeta.books || [];
  if (!books.length) return undefined;
  let bookEntry = books.find((b: BibleBook) => parseInt(b.book_id, 10) === bookNum || parseInt(b._id, 10) === bookNum);
  if (!bookEntry && bookNum > 0 && bookNum <= books.length) bookEntry = books[bookNum - 1];
  if (!bookEntry) return undefined;
  const refs = [String(bookNum), bookEntry.book_id, bookEntry._id, bookEntry.acronym_name, bookEntry.name].filter(Boolean) as string[];
  for (const ref of refs) {
    const chunk = await loadBibleBook(versionId, ref);
    if (!chunk.length) continue;
    const isNumericBook = !isNaN(parseInt(chunk[0].Book, 10)) && parseInt(chunk[0].Book, 10) > 0;
    if (isNumericBook) {
      const found = chunk.find((v: BibleVerse) => parseInt(v.Book, 10) === bookNum && parseInt(v.Chapter, 10) === chapterNum && parseInt(v.Verse, 10) === verseNum);
      if (found) return found;
      continue;
    }
    const found = chunk.find((v: BibleVerse) => v.Book === ref && parseInt(v.Chapter, 10) === chapterNum && parseInt(v.Verse, 10) === verseNum);
    if (found) return found;
  }
  const bible = await loadBibleData(versionId);
  const bookAliases = new Set([String(bookNum), bookEntry.book_id, bookEntry._id, bookEntry.name, bookEntry.acronym_name].filter(Boolean));
  return (bible.verses || []).find((v: BibleVerse) => bookAliases.has(String(v.Book)) && parseInt(v.Chapter, 10) === chapterNum && parseInt(v.Verse, 10) === verseNum);
};

const getVerseCount = async (versionId: string): Promise<number> => {
  const metaKey = getBibleMetaKey(versionId);
  const meta = DB.get(cache, metaKey) as BibleMeta | undefined;
  if (meta) return meta.totalVerses;
  const bible = await loadBibleData(versionId);
  return (bible.verses || []).length;
};

const getVerseByIndex = async (versionId: string, index: number): Promise<BibleVerse> => {
  const bible = await loadBibleData(versionId);
  const allVerses = bible.verses || [];
  return allVerses[index % allVerses.length];
};

export const getRandomVerse = async (versionId: string, dateSeed: number): Promise<BibleVerse> => {
  const count = await getVerseCount(versionId);
  const index = dateSeed % count;
  return getVerseByIndex(versionId, index);
};
